// src/services/authService.js
import { requestWithAuth } from "./api";
import { adminLevelFromBackend } from "@/constants/adminLevels";
import { logger } from "@/utils/logger";

const LOGIN_URL = "/api/auth/v1/signin/email/token";
const CHANGE_PASSWORD_URL = "/auth/v1/profile/password/set";

/**
 * Realiza login no sistema
 * @param {string} username - Email/username do usuário
 * @param {string} password - Senha do usuário
 * @returns {Promise<Object>} Dados de autenticação incluindo token e admin level
 */
export async function login(username, password) {
  let response;

  try {
    response = await fetch(LOGIN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "PTM-Client": "WebAdmin",
        "PTM-Platform": "Web",
      },
      body: JSON.stringify({
        credentials: {
          username,
          password,
        },
      }),
    });
  } catch (networkError) {
    // Erro de rede (sem internet, CORS, etc)
    logger.error("Erro de rede no login:", networkError);
    throw new Error(
      "Erro de conexão. Verifique sua internet e tente novamente.",
    );
  }

  // Trata diferentes códigos de erro HTTP
  if (!response.ok) {
    let errorMessage = "Erro ao autenticar";
    let errorData = {};

    try {
      errorData = await response.json();
    } catch {
      // Response não é JSON válido
    }

    switch (response.status) {
      case 400:
        errorMessage =
          errorData.message ||
          "Dados de login inválidos. Verifique email e senha.";
        break;
      case 401:
        errorMessage = errorData.message || "Email ou senha incorretos.";
        break;
      case 403:
        errorMessage =
          errorData.message ||
          "Acesso negado. Sua conta pode estar desabilitada.";
        break;
      case 404:
        errorMessage = "Serviço de autenticação não encontrado.";
        break;
      case 429:
        errorMessage =
          "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
        break;
      case 500:
      case 502:
      case 503:
        errorMessage =
          "Servidor temporariamente indisponível. Tente novamente em instantes.";
        break;
      default:
        errorMessage =
          errorData.message || `Erro ao autenticar (código ${response.status})`;
    }

    logger.error("Erro HTTP no login:", response.status, errorData);
    throw new Error(errorMessage);
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error("Resposta inválida do servidor. Tente novamente.");
  }

  // Valida se recebeu token
  if (!data.token) {
    throw new Error("Autenticação falhou. Token não recebido.");
  }

  // Armazena informações essenciais no localStorage
  localStorage.setItem("token", data.token);
  localStorage.setItem("refresh_token", data.refresh_token);
  // Converte profile_role_level de string para número
  const adminLevel = adminLevelFromBackend(data.profile_role_level);
  localStorage.setItem("admin_level", adminLevel);
  localStorage.setItem("admin_level_name", data.profile_role_level_name || "");

  // Armazena flag de troca de senha obrigatória
  if (data.password_change_required) {
    localStorage.setItem("password_change_required", "true");
  } else {
    localStorage.removeItem("password_change_required");
  }

  return data;
}

/**
 * Altera a senha do usuário
 * @param {string} newPassword - Nova senha
 * @returns {Promise<Object>} Resultado da operação
 */
export async function changePassword(newPassword) {
  const response = await requestWithAuth(CHANGE_PASSWORD_URL, {
    method: "POST",
    body: JSON.stringify({ password: newPassword }),
  });

  const responseData = await response.json().catch(() => ({}));

  // Mensagens que indicam que a senha já foi alterada (sucesso implícito)
  const alreadyChangedMessages = [
    "senha já foi alterada",
    "password already changed",
    "senha ja foi alterada",
  ];

  const messageText = (responseData.message || "").toLowerCase();
  const isAlreadyChanged = alreadyChangedMessages.some((msg) =>
    messageText.includes(msg),
  );

  if (!response.ok && !isAlreadyChanged) {
    throw new Error(responseData.message || "Erro ao alterar senha");
  }

  // Remove flag de troca de senha após sucesso OU se já foi alterada
  localStorage.removeItem("password_change_required");

  return { ...responseData, alreadyChanged: isAlreadyChanged };
}

/**
 * Verifica se a troca de senha é obrigatória
 * @returns {boolean}
 */
export function isPasswordChangeRequired() {
  return localStorage.getItem("password_change_required") === "true";
}

/**
 * Envia email de recuperação de senha
 * @param {string} email - Email do usuário
 * @returns {Promise<Object>} Resultado da operação
 *
 * NOTA DE SEGURANÇA: O backend retorna sucesso independentemente de o email existir ou não.
 * Isso é intencional para evitar enumeração de usuários (security best practice).
 */
export async function forgotPassword(email) {
  const response = await fetch("/api/auth/v1/signup/email/forgot", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "PTM-Client": "WebAdmin",
      "PTM-Platform": "Web",
    },
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });

  // Mesmo que o email não exista, retornamos sucesso por segurança
  // (evita enumeração de usuários)
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Se for erro de rate limiting ou server error, propagamos
    if (response.status === 429 || response.status >= 500) {
      throw new Error(
        errorData.message || "Muitas tentativas. Aguarde alguns minutos.",
      );
    }
    // Para outros erros (404, 400), retornamos sucesso por segurança
  }

  return { success: true };
}
